/**
 * Mock 的持久层。
 *
 * 为什么放在 localStorage 而不是内存：这个 Mock 的作用是"让前端在没有后端时
 * 也能完整走一遍流程"，包括**刷新页面后会话还在**。纯内存的 Mock 会让
 * `/conversations` 页面永远是空的，测不出任何真实问题。
 *
 * 为什么每条记录都要过一遍契约 schema：写进去的是**我们自己**造的数据，
 * 但如果 schema 变了而种子数据没跟上，Mock 会开始返回契约不允许的形状，
 * 于是前端在"Mock 模式"下崩溃、在"真实后端"下正常——最坏的一类假阴性。
 * 任何一条记录不合契约就整体退回种子状态，让问题立刻暴露。
 */

import {
  ConversationSchema,
  DEFAULT_PLAN_ID,
  EmailChannelSchema,
  LedgerEntrySchema,
  LlmChannelSchema,
  LlmModelRouteSchema,
  MessageSchema,
  OrderSchema,
  PLAN_LIMITS,
  ProfileSchema,
  QUOTA_RESET_TIMEZONE,
  SubscriptionSchema,
  UserSchema,
  addBillingCycle,
  isPaidPlanId,
  isSuspensionActive,
  isUnlimitedQuotaLimit,
  nextDailyResetAt,
  nextMonthlyResetAt,
  startOfUtcMonth,
  type AgeBand,
  type BillingCycle,
  type Conversation,
  type CreateAdminInput,
  type Domain,
  type EmailChannel,
  type EmailProvider,
  type GrantQuotaInput,
  type LedgerEntry,
  type LlmChannel,
  type LlmModelRole,
  type LlmModelRoute,
  type LlmProvider,
  type Message,
  type Order,
  type PatchEmailChannelInput,
  type PatchLlmChannelInput,
  type PatchLlmModelRouteInput,
  type PaymentChannel,
  type PlanId,
  type Profile,
  type Quota,
  type QuotaScope,
  type SetUserPlanInput,
  type Subscription,
  type SuspendUserInput,
  type UpsertEmailChannelInput,
  type UpsertLlmChannelInput,
  type UpsertLlmModelRouteInput,
  type User,
  type UserRole,
  type UserStatus,
} from '@spirit/contracts';

/**
 * 存储键。带版本号，形状一变就让旧数据自然失效。
 *
 * v1 → v2：用户 id 由 cuid 夹具改成纯数字字符串（与真后端 v1.4.1 的 `users.id` 自增对齐）。
 * 不升版本的话，localStorage 里那份 v1 状态会被读回来，里面的 `ckmockuser…` 过不了
 * `UserSchema` 的 `UserIdSchema`，表现成"打开页面就报响应不符合契约"。
 *
 * v2 → v3：`MockUser` 多了 `email`（邮箱验证码登录要按邮箱找人）。`userDtoOf()` 会把
 * `user.email` 放进契约的 `User`，而契约 `UserSchema.email` 是**必填键**——老状态里的
 * 账号行没有这个字段，投出去就是 `undefined`，`validate()` 会判整份状态不合契约并退回种子，
 * 表现成"升级后一刷新，会话、账本、订单全没了"。升版本等于主动换一份新状态，
 * 比让用户看着自己的数据凭空消失要好。
 *
 * v3 → v4：渠道行上的 `chatModel` / `routerModel` / `models` 三个字段搬进了新的
 * `llmModelRoutes` 表。旧状态能过校验（多出来的字段会被 zod 剥掉），但模型候选为空，
 * 于是"模型优先级"页一进去就是空表、连一条示例都没有；升版本换一份带种子的新状态，
 * 演示路径才是自洽的。
 */
const STORAGE_KEY = 'spirit.mock.state.v4';

/** Mock 的固定用户 id：所有请求都当同一个用户，Mock 不做多用户隔离。 */
export const MOCK_USER_ID = '1';

/** 默认手机号（登录页可以随便填，只有这个号码会被"识别"）。 */
export const MOCK_PHONE = '+8613800138000';

/** 登录验证码恒定值，登录页会把它显示成提示。手机与邮箱两条通道共用同一个值。 */
export const MOCK_SMS_CODE = '123456';

/**
 * 邮箱验证码的恒定值。
 *
 * 之所以不复用 `MOCK_SMS_CODE` 这个**名字**：两条通道各自演进（真后端是两个不同的
 * 验证码表）时，登录页提示里写"Mock 模式下任意 6 位都通过"的那句话不该跟着一起改。
 * 值相同只是巧合，不是约定。
 */
export const MOCK_EMAIL_CODE = '123456';

/**
 * 演示用户的邮箱。
 *
 * 种子必须给一个能用的邮箱：`/auth/email/verify` 在 Mock 里是"按邮箱找人、
 * 找不到就自动注册"，没有这条种子的话，演示用户永远走不到"已有账号"那条分支，
 * 邮箱登录就退化成"每次都建个新号"。
 */
export const MOCK_EMAIL = 'demo@example.com';

/**
 * 演示用的后台账号（ROOT 与管理员）。
 *
 * 值与 `prisma/seed.mts` 在真实库里种出来的那两个账号一致（`root`/`Root@12345`、
 * `admin`/`Admin@12345`）：两种模式共用同一对账号口令，演示时不必先想"我现在切到哪个后端了"。
 */
export const MOCK_ROOT_USERNAME = 'root';
export const MOCK_ROOT_PASSWORD = 'Root@12345';
export const MOCK_ADMIN_USERNAME = 'admin';
export const MOCK_ADMIN_PASSWORD = 'Admin@12345';

/**
 * 预置账号 id：写成常量是为了让手写请求（验收脚本 / curl）能直接引用，不必先去列表里捞。
 *
 * 值是纯数字字符串，与真后端 v1.4.1 的 `users.id`（整数自增）同形态；
 * 编号顺序 = `seedUsers()` 的数组顺序（注册顺序），所以从 1 起连续。
 */
const MOCK_ROOT_ID = '5';
const MOCK_ADMIN_ID = '4';
const MOCK_PAID_USER_ID = '2';
const MOCK_SUSPENDED_USER_ID = '3';

/**
 * 单个用户的额度计数。
 *
 * 演示用户（`MOCK_USER_ID`）**不落在**这个结构上：它的计数存在 `MockState` 顶层
 * （`dailyTokenUsed` / `monthlyTokenUsed`），因为 `/quota`、SSE 的 usage 帧与既有测试
 * 都直接读那几个字段。`quotaStateOf()` 把两种存放位置收成一个只读视图，
 * 调用方（额度快照、后台配额页）不必知道"眼前这个用户是哪一个"。
 */
export interface MockUserQuota {
  planId: PlanId;
  dailyTokenUsed: number;
  monthlyTokenUsed: number;
  dailyResetAt: string;
  monthlyResetAt: string;
}

/**
 * Mock 的账号行。
 *
 * `password` 存的是**明文**：真实后端存 scrypt 哈希，但这份状态本来就躺在 localStorage 里、
 * 打开控制台就能改，套一层哈希只是假装安全，却让"演示时该输什么密码"必须翻代码才知道。
 * 明文只活在这一层——对外投影 `userDtoOf()` 里没有 `password`，也没有 `username`
 * （契约的 `User` 只暴露 role 之后的公开字段）。
 */
export interface MockUser {
  id: string;
  role: UserRole;
  nickname: string;
  phone: string | null;
  /**
   * 邮箱（契约 `EmailSchema` 的归一形态：小写）。`null` = 没绑过邮箱。
   *
   * 普通用户与后台账号都可能为 null：手机号注册的存量用户没邮箱，
   * 后台账号也只走账号口令。`/auth/email/verify` 按它找人。
   */
  email: string | null;
  wechatOpenId: string | null;
  avatarUrl: string | null;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  /** 登录名：只有管理员 / ROOT 有；普通用户走手机号，为 null。 */
  username: string | null;
  /** 明文口令，同上；普通用户为 null。 */
  password: string | null;
  passwordUpdatedAt: string | null;
  suspendedAt: string | null;
  suspendedUntil: string | null;
  suspendReason: string | null;
  lastLoginAt: string | null;
  /** `null` = 额度不在这一行上：演示用户读顶层，管理员 / ROOT 没有个人额度。 */
  quota: MockUserQuota | null;
  /** `null` 含义同 `quota`（演示用户的订阅存在 `MockState.subscription`）。 */
  subscription: Subscription | null;
  profile: Profile | null;
}

/** 后台发放的额度。契约里叫 `AdminQuotaGrant`，这里按用户挂在 state 上。 */
export interface MockQuotaGrant {
  id: string;
  userId: string;
  totalTokens: number;
  reason: string | null;
  grantedByUserId: string | null;
  createdAt: string;
}

/**
 * Mock 的渠道行。
 *
 * 比契约 `LlmChannel` 多一个 `apiKey`：契约只下发密钥末尾提示（`apiKeyHint`），
 * 但 PATCH 的「不带 apiKey 就是不改密钥」这条语义要求 Mock 自己留着密钥。
 * 对外投影走 `llmChannelDtoOf()`，明文永远不离开这一层。
 */
export interface MockLlmChannel {
  id: string;
  name: string;
  provider: LlmProvider;
  baseUrl: string;
  apiKey: string;
  enabled: boolean;
  priority: number;
  timeoutMs: number;
  remark: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Mock 的模型路由行（v1.6.0）。
 *
 * 与后端端口层的 `LlmModelRouteRecord` 同一取舍：**不带** `channelName` /
 * `channelEnabled`，它们是渠道行的冗余展开，由 `modelRouteDtoOf()` 在投影时 join。
 * 存两份的话，渠道改名后列表会出现"两个名字"的不一致，而这种脏数据只在刷新后才看得出来。
 */
export interface MockLlmModelRoute {
  id: string;
  channelId: string;
  model: string;
  role: LlmModelRole;
  enabled: boolean;
  /**
   * 全局候选顺序的主键（升序）。
   *
   * 它是**排序字段**而不是展示用的装饰：`moveMockModelRoute()` 会按
   * `(index + 1) * REORDER_STEP` 整表重编号，理由见该函数。
   */
  priority: number;
  remark: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Mock 的邮箱渠道行。
 *
 * 与 `MockLlmChannel` 同构：比契约 `EmailChannel` 多一个 `apiKey` 明文，因为 PATCH 的
 * 「不带 apiKey 就是不改密钥」要求 Mock 自己留着它。对外投影走 `emailChannelDtoOf()`。
 */
export interface MockEmailChannel {
  id: string;
  name: string;
  provider: EmailProvider;
  baseUrl: string;
  apiKey: string;
  fromAddress: string;
  fromName: string;
  enabled: boolean;
  priority: number;
  timeoutMs: number;
  remark: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Mock 账本行。
 *
 * 比契约的 `LedgerEntry` 多几个字段：Mock 扮演的是**服务端**，而服务端必须持有真值
 * （token 量、归属）才能算出 `sharePercent` 与后台的 usage 报表（对外的响应体里不会出现它们）。
 * 校验仍然只过契约的 `LedgerEntrySchema` —— 多出来的字段不会让 `safeParse` 失败，
 * 于是"只有契约允许的字段能被下发"这条约束依然成立。
 */
export interface MockLedgerEntry extends LedgerEntry {
  totalTokens: number;
  /** 归属用户：后台 `/admin/usage` 的 user 维度按它分组。 */
  userId: string;
  /** 归属渠道；`null` = 未归因（例如一个可用渠道都没有、回复由 Mock 话术顶替时）。 */
  channelId: string | null;
  /** 归属模型；`null` 含义同上。 */
  model: string | null;
  /** 计费拆分，只有后台报表读它（用户侧端点已经把所有 token 量剥掉了）。 */
  promptTokens: number;
  completionTokens: number;
  /** 产生这笔消耗的助手消息 id：后台消息列表要按消息显示 token 量。 */
  messageId: string | null;
}

export interface MockState {
  /**
   * 演示用户的当前套餐，用来演示额度耗尽。与 `subscription` 必须同步。
   *
   * 注意：`planId` / `dailyTokenUsed` / `monthlyTokenUsed` / 两个重置时刻 / `subscription`
   * 都属于**演示用户**（手机号登录的那一个）；其它账号的同类数据存在 `MockUser` 自己那一行上。
   * 让演示用户特殊的原因是：`/quota`、SSE 的 usage 帧与既有测试都直接读这几个字段。
   * 两种存放位置由 `quotaStateOf()` 收成一个只读视图。
   */
  planId: PlanId;
  dailyTokenUsed: number;
  monthlyTokenUsed: number;
  conversations: Conversation[];
  messages: Message[];
  ledger: MockLedgerEntry[];
  /**
   * 当前订阅（与契约 `Subscription` 同形，多一个字段都要被 `validate` 拦下）。
   * `planId` 与它必须一起改，否则额度页与购买页会各说各话。
   */
  subscription: Subscription | null;
  /** 订单列表，最新的在前。 */
  orders: Order[];
  /** 已经用掉的当日额度是按哪一天算的（UTC），跨日就重置。 */
  dailyResetAt: string;
  monthlyResetAt: string;
  /** 全部账号（含演示用户）。演示用户固定是第一个。 */
  users: MockUser[];
  /**
   * 后台发放的额度：**不改** planId 的上限，只在算可用量时抵扣
   * （可用 = 上限 − (已用 − 发放)，与真实后端 `sumGrantedBetween` 的口径一致）。
   */
  grants: MockQuotaGrant[];
  /** LLM 渠道表（ROOT 可增删改，管理员只读）。 */
  llmChannels: MockLlmChannel[];
  /**
   * 模型路由表（v1.6.0）：一行 = 某渠道上的某个模型 + 用途 + 全局优先级。
   *
   * 渠道行上不再有 `chatModel` / `routerModel` / `models`，所以"这个渠道有哪些模型"
   * 只能从这里查；数组本身**不保证顺序**，对外一律走 `sortedMockModelRoutes()`。
   */
  llmModelRoutes: MockLlmModelRoute[];
  /** 邮箱渠道表（同样只有 ROOT 能动）：邮箱验证码登录靠它发信，没配就退回模拟发信。 */
  emailChannels: MockEmailChannel[];
  /**
   * 订单归属：契约的 `Order` 里没有 userId（用户只看得到自己的单），
   * 但后台要"按用户查订单"，所以在这里另存一份 `订单号 → 用户 id`。
   */
  orderOwners: Record<string, string>;
}

let state: MockState | null = null;
let idSeq = 0;

/**
 * 生成 id。
 *
 * 契约要求 8-64 字符的 cuid2 风格字符串——**不能**用 `crypto.randomUUID()`：
 * 它带连字符且长度 36，虽然能过 `IdSchema`，但混进真实数据里会让人分不清
 * 哪条是 Mock 造的。前缀 `ckmock` + 时间戳 + 序号，一眼可辨且稳定有序。
 */
export function nextId(prefix = 'ckmock'): string {
  idSeq += 1;
  const stamp = Date.now().toString(36);
  const seq = idSeq.toString(36).padStart(4, '0');
  return `${prefix}${stamp}${seq}${Math.random().toString(36).slice(2, 8)}`.slice(0, 32);
}

/**
 * 生成**用户 id**：纯数字字符串，从 1 起按注册顺序递增。
 *
 * 与真后端 v1.4.1 的 `users.id`（MySQL INT AUTO_INCREMENT）同形态，理由见
 * `packages/contracts/src/dto.ts` 的 `UserIdSchema`。取"当前最大值 + 1"而不是
 * 数组长度：后台账号能被删掉，用长度会让新账号复用已删账号的 id。
 */
export function nextUserId(users: readonly { id: string }[]): string {
  let max = 0;
  for (const user of users) {
    const value = Number(user.id);
    if (Number.isInteger(value) && value > max) max = value;
  }
  return String(max + 1);
}

/** ISO-8601 UTC（契约要求带 Z 后缀）。 */
export function isoNow(offsetMs = 0): string {
  return new Date(Date.now() + offsetMs).toISOString();
}

function seedConversation(index: number, domain: Domain, title: string, ageBand: AgeBand | null): Conversation {
  const createdAt = isoNow(-(index + 1) * 3_600_000);
  return {
    id: `ckmockseedconv000000000${index}`,
    userId: MOCK_USER_ID,
    title,
    domain,
    ageBand,
    messageCount: 2,
    lastMessageAt: createdAt,
    createdAt,
  };
}

/**
 * 种子消息 id。
 *
 * 刻意不用 `nextId()`：种子数据每次构建都必须得到**同一批 id**，
 * 否则"读存储 → 校验 → 命中种子"这条路径会在每次刷新后产生不同的 id，
 * 列表 key 全变，前端会表现成"每次刷新都整页重挂载"。
 */
function seedMessageId(index: number): string {
  return `ckmockseedmsg00000000000${index.toString().padStart(2, '0')}`;
}

function seedMessage(
  conversation: Conversation,
  index: number,
  role: 'user' | 'assistant',
  content: string,
  offsetMs: number,
): Message {
  return {
    id: seedMessageId(index),
    conversationId: conversation.id,
    role,
    content,
    domain: conversation.domain,
    ageBand: conversation.ageBand,
    safetyFlag: 'none',
    latencyMs: role === 'assistant' ? 1_260 : 0,
    upstreamModel: role === 'assistant' ? 'deepseek-chat' : null,
    createdAt: isoNow(offsetMs),
  };
}

/**
 * 账号种子：1 个 ROOT + 1 个管理员 + 3 个普通用户（含 1 个长期封号）。
 *
 * 这个构成照抄计划文档 §8.1：只有三种角色都真的存在，"角色门禁 / 封号筛选 / 后台账号页"
 * 在 Mock 下才演示得出来。普通用户只放 3 个——再多只是让列表更长，不会暴露新问题。
 */
function seedUsers(): MockUser[] {
  const now = new Date();
  const freeQuota = (): MockUserQuota => ({
    planId: DEFAULT_PLAN_ID,
    dailyTokenUsed: 0,
    monthlyTokenUsed: 0,
    dailyResetAt: nextDailyResetAt(now),
    monthlyResetAt: nextMonthlyResetAt(now),
  });

  return [
    // 演示用户：额度与订阅都存在 state 顶层（见 `MockState` 的注释），所以这两项是 null。
    {
      id: MOCK_USER_ID,
      role: 'user',
      nickname: '演示用户',
      phone: MOCK_PHONE,
      email: MOCK_EMAIL,
      wechatOpenId: null,
      avatarUrl: null,
      status: 'active',
      createdAt: isoNow(-86_400_000 * 30),
      updatedAt: isoNow(),
      username: null,
      password: null,
      passwordUpdatedAt: null,
      suspendedAt: null,
      suspendedUntil: null,
      suspendReason: null,
      lastLoginAt: isoNow(-3_600_000),
      quota: null,
      subscription: null,
      profile: {
        id: 'ckmockprofile00000000000',
        userId: MOCK_USER_ID,
        childAgeBands: ['7-12'],
        childCount: 1,
        concerns: ['family_education'],
        preferredTone: 'gentle',
        extra: {},
      },
    },
    // 已付费的普通用户：让后台的"套餐详情 / 订单列表 / 本月已发放"都有东西可看。
    {
      id: MOCK_PAID_USER_ID,
      role: 'user',
      nickname: '李女士',
      phone: '+8613900139001',
      email: 'li.nvshi@example.com',
      wechatOpenId: null,
      avatarUrl: null,
      status: 'active',
      createdAt: isoNow(-86_400_000 * 20),
      updatedAt: isoNow(-86_400_000 * 6),
      username: null,
      password: null,
      passwordUpdatedAt: null,
      suspendedAt: null,
      suspendedUntil: null,
      suspendReason: null,
      lastLoginAt: isoNow(-86_400_000 * 2),
      quota: {
        planId: 'lite',
        dailyTokenUsed: 1_200,
        monthlyTokenUsed: 34_000,
        dailyResetAt: nextDailyResetAt(now),
        monthlyResetAt: nextMonthlyResetAt(now),
      },
      subscription: {
        planId: 'lite',
        billingCycle: 'monthly',
        startsAt: isoNow(-86_400_000 * 6),
        expiresAt: addBillingCycle(isoNow(-86_400_000 * 6), 'monthly'),
        orderNo: 'SPMOCKSEED0001',
      },
      profile: null,
    },
    // 长期封号（`suspendedUntil` 为 null）：后台的封号筛选与"账号状态异常"都要有可见样本。
    {
      id: MOCK_SUSPENDED_USER_ID,
      role: 'user',
      nickname: '王先生',
      phone: '+8613700137002',
      // 封号用户刻意不给邮箱：邮箱登录走"按邮箱找人"，留一个没有邮箱的样本，
      // 才能确认封号拦截是按**账号**而不是按邮箱判的。
      email: null,
      wechatOpenId: null,
      avatarUrl: null,
      status: 'suspended',
      createdAt: isoNow(-86_400_000 * 12),
      updatedAt: isoNow(-86_400_000 * 2),
      username: null,
      password: null,
      passwordUpdatedAt: null,
      suspendedAt: isoNow(-86_400_000 * 2),
      suspendedUntil: null,
      suspendReason: '多次发布违规内容（演示数据）',
      lastLoginAt: isoNow(-86_400_000 * 3),
      quota: freeQuota(),
      subscription: null,
      profile: null,
    },
    {
      id: MOCK_ADMIN_ID,
      role: 'admin',
      nickname: '演示管理员',
      phone: null,
      email: null,
      wechatOpenId: null,
      avatarUrl: null,
      status: 'active',
      createdAt: isoNow(-86_400_000 * 45),
      updatedAt: isoNow(-86_400_000),
      username: MOCK_ADMIN_USERNAME,
      password: MOCK_ADMIN_PASSWORD,
      passwordUpdatedAt: isoNow(-86_400_000 * 9),
      suspendedAt: null,
      suspendedUntil: null,
      suspendReason: null,
      lastLoginAt: isoNow(-86_400_000),
      quota: null,
      subscription: null,
      profile: null,
    },
    {
      id: MOCK_ROOT_ID,
      role: 'root',
      nickname: 'ROOT',
      phone: null,
      email: null,
      wechatOpenId: null,
      avatarUrl: null,
      status: 'active',
      createdAt: isoNow(-86_400_000 * 60),
      updatedAt: isoNow(-86_400_000 * 30),
      username: MOCK_ROOT_USERNAME,
      password: MOCK_ROOT_PASSWORD,
      passwordUpdatedAt: isoNow(-86_400_000 * 30),
      suspendedAt: null,
      suspendedUntil: null,
      suspendReason: null,
      lastLoginAt: null,
      quota: null,
      subscription: null,
      profile: null,
    },
  ];
}

/**
 * 渠道种子：一个启用（priority 10）、一个停用（priority 100）。
 *
 * 两种状态都要有：后台渠道页的启用/停用开关，以及 `LlmChannelList.activeChannelId`
 * （取**候选顺序**里的第一条对话模型所属渠道，见 `llmChannelListOf()`）
 * 只有在列表里同时存在两种状态时才看得出算得对不对。
 */
function seedLlmChannels(): MockLlmChannel[] {
  const createdAt = isoNow(-86_400_000 * 10);
  return [
    {
      id: 'ckmockchan0000000000000',
      name: '主渠道（演示）',
      provider: 'openai_compatible',
      baseUrl: 'https://api.deepseek.com/v1',
      // 刻意不用 `sk-` 前缀：仓库门禁（scripts/check-secrets.mjs 检查 3/4）把任何
      // `sk-` 开头的字面量视为密钥泄漏并按构建失败处理，Mock 的假密钥也不例外。
      apiKey: 'mock-primary-key-0001',
      enabled: true,
      priority: 10,
      timeoutMs: 60_000,
      remark: 'Mock 预置渠道',
      createdAt,
      updatedAt: createdAt,
    },
    {
      id: 'ckmockchan0000000000001',
      name: '备用渠道（演示）',
      provider: 'openai_compatible',
      baseUrl: 'https://api.moonshot.cn/v1',
      apiKey: 'mock-backup-key-0002',
      enabled: false,
      priority: 100,
      timeoutMs: 30_000,
      remark: null,
      createdAt,
      updatedAt: createdAt,
    },
  ];
}

/**
 * 模型路由种子（v1.6.0）：两条渠道共 3 行，覆盖页面要区分的四种状态。
 *
 * 具体覆盖：主渠道上一条 `role='both'`（同时供对话与意图路由，顶部卡片的
 * `activeChatModel` 与 `activeRouterModel` 因此都非空）、一条只做对话的备用模型
 * （演示"降级候选"）；停用渠道上一条停用路由（表格里同时出现「渠道已停用」与「已停用」）。
 *
 * 优先级刻意给 10 / 20 / 30：等值的话 `move` 之后看不出顺序变化，
 * 而"点了上移没反应"正是后端 `REORDER_STEP` 那段注释要避免的现象。
 */
function seedLlmModelRoutes(): MockLlmModelRoute[] {
  const createdAt = isoNow(-86_400_000 * 9);
  return [
    {
      id: 'ckmockroute0000000000000',
      channelId: 'ckmockchan0000000000000',
      model: 'deepseek-chat',
      role: 'both',
      enabled: true,
      priority: 10,
      remark: '主力模型，同时承担意图路由',
      createdAt,
      updatedAt: createdAt,
    },
    {
      id: 'ckmockroute0000000000001',
      channelId: 'ckmockchan0000000000000',
      model: 'deepseek-reasoner',
      role: 'chat',
      enabled: true,
      priority: 20,
      remark: '复杂题的第一顺位降级候选',
      createdAt,
      updatedAt: createdAt,
    },
    {
      id: 'ckmockroute0000000000002',
      channelId: 'ckmockchan0000000000001',
      model: 'moonshot-v1-8k',
      role: 'chat',
      enabled: false,
      priority: 30,
      remark: '备用渠道停用中，配置保留',
      createdAt,
      updatedAt: createdAt,
    },
  ];
}

/**
 * 邮箱渠道种子：一个启用（priority 10）、一个停用（priority 100）。
 *
 * 启用那条决定后台概览里 `email.source` 是 `'channel'` 还是 `'mock'`，
 * 也决定登录页发出的验证码是"走渠道"还是"只打印在控制台"。默认给一条启用的：
 * 演示邮箱登录时要能一眼看到"渠道已配置"，否则每次都要手点一遍新增才知道页面能用。
 */
function seedEmailChannels(): MockEmailChannel[] {
  const createdAt = isoNow(-86_400_000 * 8);
  return [
    {
      id: 'ckmockmailchan0000000000',
      name: '主发信渠道（演示）',
      provider: 'http_json',
      baseUrl: 'https://api.resend.com',
      // 同 `seedLlmChannels()`：不能写 `sk-` 前缀，仓库密钥门禁会把任何 `sk-` 字面量
      // 当泄漏处理，Mock 的假密钥也不例外。
      apiKey: 'mock-email-key-0001',
      fromAddress: 'no-reply@example.com',
      fromName: '智家教育',
      enabled: true,
      priority: 10,
      timeoutMs: 15_000,
      remark: 'Mock 预置邮箱渠道',
      createdAt,
      updatedAt: createdAt,
    },
    {
      id: 'ckmockmailchan0000000001',
      name: '备用发信渠道（演示）',
      provider: 'http_json',
      baseUrl: 'https://api.mailgun.example.com',
      apiKey: 'mock-email-key-0002',
      fromAddress: 'support@example.com',
      fromName: '智家教育客服',
      enabled: false,
      priority: 100,
      timeoutMs: 30_000,
      remark: null,
      createdAt,
      updatedAt: createdAt,
    },
  ];
}

function buildSeed(): MockState {
  const seedDefs: Array<{ domain: Domain; title: string; ageBand: AgeBand | null; q: string; a: string }> = [
    {
      domain: 'family_education',
      title: '孩子写作业拖拉',
      ageBand: '7-12',
      q: '我家孩子写作业总是拖拉，催他就发脾气，怎么办？',
      a: '先别急着批评他。**拖拉**往往是启动困难，而不是态度问题。可以试试把作业切成 10 分钟一段，每段结束让他自己打个勾——看得见的进度比催促更能启动一个孩子。',
    },
    {
      domain: 'psychology',
      title: '总是睡不好',
      ageBand: null,
      q: '最近工作压力大，晚上总是睡不着，脑子里停不下来。',
      a: '躺着焦虑和坐着焦虑是两件事。**离开床**、到暗一点的地方坐 10 分钟，等困意回来再躺下，比在床上硬躺更有效。如果连续两周都这样，建议做一次专业的睡眠评估。',
    },
  ];

  const conversations: Conversation[] = [];
  const messages: Message[] = [];

  seedDefs.forEach((def, index) => {
    const conversation = seedConversation(index, def.domain, def.title, def.ageBand);
    conversations.push(conversation);
    const base = -(index + 1) * 3_600_000;
    messages.push(seedMessage(conversation, index * 2, 'user', def.q, base));
    messages.push(seedMessage(conversation, index * 2 + 1, 'assistant', def.a, base + 2_000));
  });

  return {
    planId: DEFAULT_PLAN_ID,
    dailyTokenUsed: 6_550,
    monthlyTokenUsed: 48_200,
    conversations,
    messages,
    ledger: [],
    subscription: null,
    orders: [],
    dailyResetAt: nextDailyResetAt(new Date()),
    monthlyResetAt: nextMonthlyResetAt(new Date()),
    users: seedUsers(),
    /*
     * 一笔发放记录：后台配额页的"本月已发放"与 `grants` 列表如果永远是空的，
     * 就看不出 `剩余 = 上限 − (已用 − 发放)` 这条口径到底有没有算进去。
     */
    grants: [
      {
        id: 'ckmockgrant0000000000000',
        userId: MOCK_PAID_USER_ID,
        totalTokens: 2_000_000,
        reason: '运营补偿（演示数据）',
        grantedByUserId: MOCK_ROOT_ID,
        createdAt: isoNow(-86_400_000 * 3),
      },
    ],
    llmChannels: seedLlmChannels(),
    llmModelRoutes: seedLlmModelRoutes(),
    emailChannels: seedEmailChannels(),
    orderOwners: {},
  };
}

/** 逐条校验；任意一条不合契约就整体作废。 */
function validate(candidate: MockState): boolean {
  if (!candidate.conversations.every((item) => ConversationSchema.safeParse(item).success)) {
    return false;
  }
  if (!candidate.messages.every((item) => MessageSchema.safeParse(item).success)) {
    return false;
  }
  if (!candidate.ledger.every((item) => LedgerEntrySchema.safeParse(item).success)) {
    return false;
  }
  // 订阅与订单也走契约 schema：Mock 造出来的形状必须和真实后端一模一样，
  // 否则前端会在 Mock 下"能跑"、连上真服务就崩，这种偏差最难查。
  if (candidate.subscription !== null && !SubscriptionSchema.safeParse(candidate.subscription).success) {
    return false;
  }
  // 账号与渠道也过契约，但校验的是**对外投影**（`userDtoOf` / `llmChannelDtoOf`）：
  // 前端真正解析的是投影后的形状，拿原始行去校验没有意义（原始行还多着明文口令等字段）。
  const usersValid = candidate.users.every(
    (item) =>
      UserSchema.safeParse(userDtoOf(item)).success &&
      (item.profile === null || ProfileSchema.safeParse(item.profile).success),
  );
  if (!usersValid) {
    return false;
  }
  if (!candidate.llmChannels.every((item) => LlmChannelSchema.safeParse(llmChannelDtoOf(item)).success)) {
    return false;
  }
  // 模型路由校验的也是投影（`channelName` / `channelEnabled` 是 join 出来的）；
  // 渠道不存在的行直接跳过：它对外本来就不可见（与仓储 `list()` 一致）。
  const routes: MockLlmModelRoute[] = candidate.llmModelRoutes;
  const routeValid = routes.every((item) => {
    const channel = candidate.llmChannels.find((entry) => entry.id === item.channelId);
    return channel === undefined || LlmModelRouteSchema.safeParse(modelRouteDtoOf(item, channel)).success;
  });
  if (!routeValid) {
    return false;
  }
  // 邮箱渠道同理：校验的是对外的 `EmailChannel` 投影（明文密钥不在其中）。
  if (!candidate.emailChannels.every((item) => EmailChannelSchema.safeParse(emailChannelDtoOf(item)).success)) {
    return false;
  }
  return candidate.orders.every((item) => OrderSchema.safeParse(item).success);
}

function readStored(): MockState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as MockState;
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    if (!Array.isArray(parsed.conversations) || !Array.isArray(parsed.messages)) {
      return null;
    }
    if (!Array.isArray(parsed.ledger)) {
      parsed.ledger = [];
    } else {
      // 账本行的归属字段是后加的：老状态里没有它们，缺失会让后台 usage 报表
      // 分不出组（`key` 落到 undefined，违反契约的"key 必须是字符串"）。
      parsed.ledger = parsed.ledger.map((entry) => ({
        ...entry,
        userId: typeof entry.userId === 'string' ? entry.userId : MOCK_USER_ID,
        channelId: entry.channelId ?? null,
        model: entry.model ?? null,
        promptTokens: typeof entry.promptTokens === 'number' ? entry.promptTokens : 0,
        completionTokens: typeof entry.completionTokens === 'number' ? entry.completionTokens : 0,
        messageId: entry.messageId ?? null,
      }));
    }
    // 支付功能上线前存下来的状态没有这两个键：补默认值而不是丢弃整份数据，
    // 免得用户一升级就被清空会话。
    if (!Array.isArray(parsed.orders)) {
      parsed.orders = [];
    }
    if (parsed.subscription === undefined) {
      parsed.subscription = null;
    }
    // 账号体系上线前存下来的状态没有 users：补种子账号而不是丢弃整份数据，
    // 否则升级一次就把用户的会话、账本、订单一起清了（会话的 userId 还指着演示用户）。
    if (!Array.isArray(parsed.users) || parsed.users.length === 0) {
      parsed.users = seedUsers();
    }
    // 邮箱是后加的字段：老状态的账号行没有它，`userDtoOf()` 投出去就是 `undefined`，
    // 而契约 `UserSchema.email` 是必填键——补 null，而不是让 `validate()` 把整份状态判死。
    parsed.users = parsed.users.map((user) => ({
      ...user,
      email: typeof user.email === 'string' ? user.email : null,
    }));
    if (!Array.isArray(parsed.grants)) {
      parsed.grants = [];
    }
    if (!Array.isArray(parsed.llmChannels) || parsed.llmChannels.length === 0) {
      parsed.llmChannels = seedLlmChannels();
    }
    // v1.6.0 的模型路由表：旧状态（或手工改过的状态）里没有这个数组，补种子而不是留空，
    // 否则"模型优先级"页打开就是一张空表，看起来像功能坏了。
    if (!Array.isArray(parsed.llmModelRoutes) || parsed.llmModelRoutes.length === 0) {
      parsed.llmModelRoutes = seedLlmModelRoutes();
    }
    // 邮箱渠道同理：存储键已经升到 v3，这里仍然补一次，是因为"某次写入只带了半边状态"
    // 也会走到这条路径（例如扩展/脚本手工改过 localStorage）。
    if (!Array.isArray(parsed.emailChannels) || parsed.emailChannels.length === 0) {
      parsed.emailChannels = seedEmailChannels();
    }
    if (typeof parsed.orderOwners !== 'object' || parsed.orderOwners === null) {
      parsed.orderOwners = {};
    }
    // 账号体系之前下的单没有归属记录：那时候只有演示用户能下单，补上而不是丢掉。
    for (const order of parsed.orders) {
      parsed.orderOwners[order.orderNo] = parsed.orderOwners[order.orderNo] ?? MOCK_USER_ID;
    }
    return validate(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persist(): void {
  if (typeof window === 'undefined' || state === null) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // 隐私模式下写不进去：本次会话仍然可用，只是刷新后回到种子状态。
  }
}

/**
 * 取当前状态（首次调用时从存储恢复或建种子）。
 *
 * 顺带做**跨日/跨月重置**：日额度按 UTC 自然日切分（`QUOTA_RESET_TIMEZONE`），
 * 所以"重置"不是定时器，而是每次访问时比对时间戳。没有这一步的话，
 * 一个昨天把额度用光的 Mock 会永远显示"额度已用完"，让人以为代码坏了。
 */
export function getMockState(): MockState {
  if (state === null) {
    state = readStored() ?? buildSeed();
  }

  const now = new Date();
  if (new Date(state.dailyResetAt).getTime() <= now.getTime()) {
    state.dailyTokenUsed = 0;
    state.dailyResetAt = nextDailyResetAt(now);
  }
  if (new Date(state.monthlyResetAt).getTime() <= now.getTime()) {
    state.monthlyTokenUsed = 0;
    state.monthlyResetAt = nextMonthlyResetAt(now);
  }
  // 其它账号的计数存在自己那一行上（见 `MockState` 的注释），同一套跨日/跨月规则也要跑一遍，
  // 否则后台看一个"昨天用光"的账号会永远显示额度耗尽。
  for (const user of state.users) {
    const quota = user.quota;
    if (quota === null) {
      continue;
    }
    if (new Date(quota.dailyResetAt).getTime() <= now.getTime()) {
      quota.dailyTokenUsed = 0;
      quota.dailyResetAt = nextDailyResetAt(now);
    }
    if (new Date(quota.monthlyResetAt).getTime() <= now.getTime()) {
      quota.monthlyTokenUsed = 0;
      quota.monthlyResetAt = nextMonthlyResetAt(now);
    }
  }

  return state;
}

/** 写回存储（每次修改后调用）。 */
export function saveMockState(): void {
  persist();
}

/** 清空并重建种子（"重置 Mock 数据"入口用）。 */
export function resetMockState(): MockState {
  state = buildSeed();
  persist();
  return state;
}

/**
 * 内部真值 → 对外快照（契约 §2 `Quota`，v1.3.0）。
 *
 * **刻意自己实现而不是 import 契约的 `quotaSnapshotOf()`**：Mock 扮演的是服务端，
 * 而 `quotaSnapshotOf` 与 `PLAN_LIMITS` 在契约里被标成了"服务端内部"。
 * 这里照抄它的规则（两维里剩余比例更小者胜 / 向下取整 / 夹在 0-100 /
 * 两维都不设闸才 `unlimited`），因为 Mock 的价值恰恰在于"用另一份实现去撞真后端"：
 * 直接复用同一个函数会让两边的口径偏差在 Mock 模式下永远测不出来。
 *
 * 规则细节与契约一致：
 * - 剩余比例更小者胜（用户最先撞上的那一维）；两者相等时取日维度（重置更近）。
 * - `remainingPercent` **向下取整**并夹在 `[0,100]`：宁可少报剩余，也不虚报。
 * - 两个维度都无闸（内部 `unlimited` 档）时才 `unlimited: true`，
 *   此时按契约固定报 `scope: 'monthly'` + `remainingPercent: 100` + 月度重置时刻。
 */
export function snapshotOf(
  planId: PlanId,
  dailyTokenUsed: number,
  monthlyTokenUsed: number,
  now: Date,
): Quota {
  const limits = PLAN_LIMITS[planId];
  const dailyRatio = ratioOrNull(limits.dailyTokenLimit, dailyTokenUsed);
  const monthlyRatio = ratioOrNull(limits.monthlyTokenLimit, monthlyTokenUsed);

  if (dailyRatio === null && monthlyRatio === null) {
    return {
      planId,
      scope: 'monthly',
      remainingPercent: 100,
      unlimited: true,
      resetAt: nextMonthlyResetAt(now),
    };
  }

  const useDaily = dailyRatio !== null && (monthlyRatio === null || dailyRatio <= monthlyRatio);
  const scope: QuotaScope = useDaily ? 'daily' : 'monthly';
  const ratio = useDaily ? (dailyRatio ?? 0) : (monthlyRatio ?? 0);

  return {
    planId,
    scope,
    remainingPercent: Math.min(100, Math.max(0, Math.floor(ratio * 100))),
    unlimited: false,
    resetAt: scope === 'daily' ? nextDailyResetAt(now) : nextMonthlyResetAt(now),
  };
}

/** 剩余比例；上限缺失/为 0（配置异常，除零无意义）或哨兵时返回 null（该维不设闸）。 */
function ratioOrNull(limit: number, used: number): number | null {
  if (limit <= 0 || isUnlimitedQuotaLimit(limit)) {
    return null;
  }
  return Math.max(0, Math.min(1, (limit - used) / limit));
}

/** 指定维度的剩余百分比（0-100 整数，向下取整，超发夹 0）。给 42900 的 details 用。 */
function percentOf(limit: number, used: number): number {
  const ratio = ratioOrNull(limit, used);
  return ratio === null ? 100 : Math.min(100, Math.max(0, Math.floor(ratio * 100)));
}

/** 当前额度快照。 */
export function currentQuota(): Quota {
  const current = getMockState();
  return snapshotOf(current.planId, current.dailyTokenUsed, current.monthlyTokenUsed, new Date());
}

/**
 * 一笔消耗的归属信息。
 *
 * 必填（不给默认值）：漏传会让账本行落进"未归因"，而后台 usage 报表正是靠它分组——
 * 与其静默丢归属，不如让调用方编译不过。
 */
export interface MockUsageAttribution {
  userId: string;
  channelId: string | null;
  model: string | null;
  promptTokens: number;
  completionTokens: number;
  messageId: string | null;
}

/**
 * 记一笔消耗（结算行）。
 *
 * 服务端是两阶段（先 `reserve` 后 `commit`），Mock 只落 `commit` 一行：真实后端
 * `GET /quota/ledger` 目前也只回结算行，Mock 多造一种 `kind` 反而会让前端的
 * "按次"视图在 Mock 下看到真机上永远不会出现的东西。
 *
 * `totalTokens` 与归属字段只写进 Mock 的账本（它扮演"服务端"），用户侧响应体里会被剥掉。
 */
export function recordUsage(
  conversationId: string | null,
  totalTokens: number,
  attribution: MockUsageAttribution,
): void {
  const current = getMockState();
  if (attribution.userId === MOCK_USER_ID) {
    current.dailyTokenUsed += totalTokens;
    current.monthlyTokenUsed += totalTokens;
  } else {
    // 非演示用户的计数在自己那一行上（见 `MockState` 的注释）。
    const quota = findMockUser(attribution.userId)?.quota;
    if (quota !== null && quota !== undefined) {
      quota.dailyTokenUsed += totalTokens;
      quota.monthlyTokenUsed += totalTokens;
    }
  }
  current.ledger.unshift({
    id: nextId('ckmockledger'),
    conversationId,
    kind: 'commit',
    totalTokens,
    createdAt: isoNow(),
    ...attribution,
  });
  persist();
}

/**
 * 套餐额度耗尽，用于演示 42900。
 *
 * 要顶到**真正能拦住请求的那一维**：免费档的日维度是哨兵（"不设每日上限"），
 * 把日用量设成哨兵的话，日维度的剩余比例会算成 null（该维不设闸），请求照样
 * 发得出去——看起来就是"额度用完了但没被拦"，比不演示还糟。
 */
export function exhaustQuota(): void {
  const current = getMockState();
  const limits = PLAN_LIMITS[current.planId];
  if (isUnlimitedQuotaLimit(limits.dailyTokenLimit)) {
    current.monthlyTokenUsed = limits.monthlyTokenLimit;
  } else {
    current.dailyTokenUsed = limits.dailyTokenLimit;
  }
  persist();
}

/**
 * 42900 的 `details`（契约 §4.4，v1.3.0）。
 *
 * 必须报出**被拦的那一维**：前端会照 `scope` 显示「今日额度已用完」或「本月额度已用完」，
 * 说错维度会让用户白等一天。这里判的不是"最先撞上的一维"（那是 `snapshotOf` 的活），
 * 而是"预扣失败的那一维"——两者在额度刚好用尽的那一刻可能不同。
 */
export function quotaExceededDetails(): {
  scope: QuotaScope;
  remainingPercent: number;
  resetAt: string;
} {
  const current = getMockState();
  const limits = PLAN_LIMITS[current.planId];
  if (isUnlimitedQuotaLimit(limits.dailyTokenLimit)) {
    return {
      scope: 'monthly',
      remainingPercent: percentOf(limits.monthlyTokenLimit, current.monthlyTokenUsed),
      resetAt: current.monthlyResetAt,
    };
  }
  return {
    scope: 'daily',
    remainingPercent: percentOf(limits.dailyTokenLimit, current.dailyTokenUsed),
    resetAt: current.dailyResetAt,
  };
}

/**
 * 开通/覆盖订阅。
 *
 * `planId` 与 `subscription` 必须**一起**改：额度快照读 `planId`，支付页读 `subscription`，
 * 只改一个就会出现"额度页说 Pro、购买页说 Free（未开通）"的矛盾状态。
 */
export function activateSubscription(subscription: Subscription | null): void {
  const current = getMockState();
  current.subscription = subscription;
  current.planId = subscription?.planId ?? DEFAULT_PLAN_ID;
  persist();
}

/** 当前订阅（过期的也照原样返回：是否仍生效由契约的 `effectivePlanId` 判）。 */
export function currentSubscription(): Subscription | null {
  return getMockState().subscription;
}

/**
 * 订单号：`SP` + 10-30 位大写字母数字（`OrderNoSchema`）。
 *
 * 不能复用 `nextId()`——它是小写字母数字，`OrderNoSchema` 会直接拒。
 */
export function nextOrderNo(): string {
  idSeq += 1;
  const stamp = Date.now().toString(36).toUpperCase();
  const seq = idSeq.toString(36).toUpperCase().padStart(4, '0');
  const noise = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `SP${stamp}${seq}${noise}`;
}

/** 按订单号查订单（Mock 的"订单表"就是内存里的数组）。 */
export function findMockOrder(orderNo: string): Order | null {
  return getMockState().orders.find((item) => item.orderNo === orderNo) ?? null;
}

/**
 * 找一笔可以复用的待支付订单（同档位 / 同周期 / 同渠道，且未过期）。
 *
 * 真实后端也这么做：用户连点两次"立即支付"应该看到同一笔订单的同一个二维码，
 * 而不是被扣两次钱。
 */
export function findPendingMockOrder(
  planId: PlanId,
  billingCycle: BillingCycle,
  channel: PaymentChannel,
  now: Date,
): Order | null {
  const current = getMockState();
  return (
    current.orders.find(
      (item) =>
        item.planId === planId &&
        item.billingCycle === billingCycle &&
        item.channel === channel &&
        item.status === 'pending' &&
        Date.parse(item.payExpiresAt) > now.getTime(),
    ) ?? null
  );
}

/**
 * 写入订单：同号覆盖，新订单放最前。
 *
 * 顺带记下归属（`orderOwners`）：契约的 `Order` 没有 userId（用户只看得到自己的单），
 * 但后台要"按用户查订单"。用 `??=` 保住第一次写入的归属——支付时重写同一笔订单
 * 不该把归属改掉。
 */
export function saveMockOrder(order: Order, userId: string = MOCK_USER_ID): void {
  const current = getMockState();
  const index = current.orders.findIndex((item) => item.orderNo === order.orderNo);
  if (index >= 0) {
    current.orders[index] = order;
  } else {
    current.orders.unshift(order);
  }
  current.orderOwners[order.orderNo] = current.orderOwners[order.orderNo] ?? userId;
  persist();
}

/**
 * 把已过支付时限的待支付订单标成 `closed`。
 *
 * 真实后端靠定时任务收尾；Mock 里没有定时器，就在每次访问订单时顺手结算，
 * 保证"过期的订单不会还能被支付"。
 */
export function expireMockOrders(now: Date): void {
  const current = getMockState();
  let changed = false;
  current.orders = current.orders.map((item) => {
    if (item.status === 'pending' && Date.parse(item.payExpiresAt) <= now.getTime()) {
      changed = true;
      return { ...item, status: 'closed' as const };
    }
    return item;
  });
  if (changed) {
    persist();
  }
}

/**
 * 切套餐（演示入口）。
 *
 * 现在它会**同时**写订阅：付费档造一个从此刻起算的订阅窗口，免费/内部档清空订阅。
 * 只改 `planId` 的话，切到 Pro 之后支付页仍会显示"未开通"并让你再买一次。
 */
export function setPlan(planId: PlanId): void {
  if (!isPaidPlanId(planId)) {
    activateSubscription(null);
    return;
  }
  const cycle: BillingCycle = getMockState().subscription?.billingCycle ?? 'monthly';
  const startsAt = isoNow();
  activateSubscription({
    planId,
    billingCycle: cycle,
    startsAt,
    expiresAt: addBillingCycle(startsAt, cycle),
    orderNo: nextOrderNo(),
  });
}

/* ------------------------------------------------------------------ *
 * 账号与令牌
 * ------------------------------------------------------------------ */

const ACCESS_TOKEN_PREFIX = 'mock-access-token-';
const REFRESH_TOKEN_PREFIX = 'mock-refresh-token-';

/**
 * 既有测试与老 localStorage 里的固定令牌（没有用户后缀）。
 *
 * 它必须继续被识别成**演示用户**：`mock-ledger.test.ts` 与 `mock-payment.test.ts`
 * 都用 `Bearer mock-access-token-test` 发请求（Mock 不做真鉴权，这条一直是"形状合法的令牌"），
 * 改掉就等于悄悄改了那两个测试的语义。
 */
const LEGACY_ACCESS_TOKEN = 'mock-access-token-test';

/** 对外投影：契约 `User`。`password` / `username` / 封号明细都不在这里。 */
export function userDtoOf(user: MockUser): User {
  return {
    id: user.id,
    role: user.role,
    phone: user.phone,
    email: user.email,
    wechatOpenId: user.wechatOpenId,
    nickname: user.nickname,
    avatarUrl: user.avatarUrl,
    status: user.status,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
  };
}

/**
 * 对外投影：契约 `LlmChannel`。只给末尾提示 `apiKeyHint`，明文永不下发。
 *
 * v1.6.0 起这里**不再**有 `chatModel` / `routerModel` / `models`：模型属于模型路由表，
 * 渠道 DTO 只回答"这条渠道怎么连"。
 */
export function llmChannelDtoOf(channel: MockLlmChannel): LlmChannel {
  return {
    id: channel.id,
    name: channel.name,
    provider: channel.provider,
    baseUrl: channel.baseUrl,
    enabled: channel.enabled,
    priority: channel.priority,
    timeoutMs: channel.timeoutMs,
    apiKeyHint: `****${channel.apiKey.slice(-4)}`,
    remark: channel.remark,
    createdAt: channel.createdAt,
    updatedAt: channel.updatedAt,
  };
}

/**
 * 对外投影：契约 `LlmModelRoute`。
 *
 * `channelName` / `channelEnabled` 是**渠道行的展开**（后端由仓储 join），调用方必须
 * 把渠道行一起传进来：页面要在同一行里看到"这条模型挂在谁身上、那条渠道是否停用"。
 */
export function modelRouteDtoOf(route: MockLlmModelRoute, channel: MockLlmChannel): LlmModelRoute {
  return {
    id: route.id,
    channelId: route.channelId,
    channelName: channel.name,
    channelEnabled: channel.enabled,
    model: route.model,
    role: route.role,
    enabled: route.enabled,
    priority: route.priority,
    remark: route.remark,
    createdAt: route.createdAt,
    updatedAt: route.updatedAt,
  };
}

/** 对外投影：契约 `EmailChannel`。同样只给末尾 4 位提示，明文密钥不下发。 */
export function emailChannelDtoOf(channel: MockEmailChannel): EmailChannel {
  return {
    id: channel.id,
    name: channel.name,
    provider: channel.provider,
    baseUrl: channel.baseUrl,
    fromAddress: channel.fromAddress,
    fromName: channel.fromName,
    enabled: channel.enabled,
    priority: channel.priority,
    timeoutMs: channel.timeoutMs,
    apiKeyHint: `****${channel.apiKey.slice(-4)}`,
    remark: channel.remark,
    createdAt: channel.createdAt,
    updatedAt: channel.updatedAt,
  };
}

export function findMockUser(userId: string): MockUser | null {
  return getMockState().users.find((item) => item.id === userId) ?? null;
}

/**
 * 按邮箱查（先 trim + 小写：契约 `EmailSchema` 就是这么归一后再存的）。
 *
 * `email === null` 的行必须排掉：`null === null` 会让"没绑邮箱"的账号被任意空输入命中。
 */
export function findMockUserByEmail(email: string): MockUser | null {
  const normalized = email.trim().toLowerCase();
  return getMockState().users.find((item) => item.email !== null && item.email === normalized) ?? null;
}

/**
 * 按邮箱找人，找不到就注册一个普通用户。
 *
 * Mock 必须复刻"未注册的邮箱自动建号"这条语义，否则邮箱登录在本地只能用演示邮箱试，
 * 而真后端是能建号的——这种差异会让前端把"新用户注册成功"的落地页写错。
 * 新账号照 `createMockAdmin()` 的写法：id 走 `nextUserId()`（纯数字字符串），
 * 额度 / 订阅 / 资料都留 null（`quotaStateOf()` 会给一个免费额度视图）。
 */
export function ensureMockUserByEmail(email: string, now: Date): MockUser {
  const existing = findMockUserByEmail(email);
  if (existing !== null) {
    return existing;
  }
  const normalized = email.trim().toLowerCase();
  const state = getMockState();
  const user: MockUser = {
    id: nextUserId(state.users),
    role: 'user',
    nickname: normalized.split('@')[0]?.slice(0, 32) || '邮箱用户',
    phone: null,
    email: normalized,
    wechatOpenId: null,
    avatarUrl: null,
    status: 'active',
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    username: null,
    password: null,
    passwordUpdatedAt: null,
    suspendedAt: null,
    suspendedUntil: null,
    suspendReason: null,
    lastLoginAt: null,
    quota: null,
    subscription: null,
    profile: null,
  };
  state.users.push(user);
  persist();
  return user;
}

/** 按登录名查（先归一成小写：契约的 `UsernameSchema` 就是这么存的）。 */
export function findMockUserByUsername(username: string): MockUser | null {
  const normalized = username.trim().toLowerCase();
  return getMockState().users.find((item) => item.username === normalized) ?? null;
}

/** 把 userId 翻成昵称（后台列表要显示 `userNickname`）。 */
export function nicknameOf(userId: string): string | null {
  return findMockUser(userId)?.nickname ?? null;
}

/**
 * 封号是否**此刻**生效。
 *
 * 直接复用契约的 `isSuspensionActive`：真实后端的守卫用的就是它。Mock 自己再实现一遍的话，
 * "短期封号到期自动恢复"这条最容易写错的语义会在两个模式之间分叉。
 */
export function isUserSuspended(user: MockUser, now: Date): boolean {
  return isSuspensionActive(
    user.status,
    user.suspendedUntil === null ? null : new Date(user.suspendedUntil),
    now,
  );
}

/**
 * 有效状态：`status === 'suspended'` 但封号已到期时算 `active`。
 *
 * 真实后端**不会**定时把 `status` 改回来（懒判定），所以后台返回两个字段：
 * `status` 是库里的原值，`effectiveStatus` 是"此刻算不算被封"。
 */
export function effectiveStatusOf(user: MockUser, now: Date): UserStatus {
  if (user.status !== 'suspended') {
    return user.status;
  }
  return isUserSuspended(user, now) ? 'suspended' : 'active';
}

/** 登录成功后写一次 `lastLoginAt`（后台列表要展示"最近登录"）。 */
export function touchLastLogin(userId: string): void {
  const user = findMockUser(userId);
  if (user === null) {
    return;
  }
  user.lastLoginAt = isoNow();
  user.updatedAt = isoNow();
  persist();
}

/**
 * 发一对 Mock 令牌。
 *
 * 令牌里**带 userId**：取数层要认出"这次是谁"，才能判角色、才能让 `/auth/me`
 * 对管理员返回 `quota: null`。Mock 不做真鉴权（不签名、不校验过期），但"令牌对应哪个账号"
 * 这件事必须是真的，否则后台门禁就只是装饰。
 */
export function issueMockTokens(userId: string): { accessToken: string; refreshToken: string; expiresIn: number } {
  return {
    accessToken: `${ACCESS_TOKEN_PREFIX}${userId}-${nextId('')}`,
    refreshToken: `${REFRESH_TOKEN_PREFIX}${userId}-${nextId('')}`,
    expiresIn: 7_200,
  };
}

function userIdOfToken(token: string | null, prefix: string): string | null {
  if (token === null || !token.startsWith(prefix)) {
    return null;
  }
  const rest = token.slice(prefix.length);
  // userId 全是小写字母数字（不含 `-`），随机段也不含：最后一个 `-` 就是分界。
  const sep = rest.lastIndexOf('-');
  if (sep <= 0) {
    return null;
  }
  const userId = rest.slice(0, sep);
  return findMockUser(userId) === null ? null : userId;
}

/** access token → 用户 id；认不出来返回 null（取数层据此回 40100）。 */
export function userIdOfAccessToken(token: string | null): string | null {
  if (token === LEGACY_ACCESS_TOKEN) {
    return MOCK_USER_ID;
  }
  return userIdOfToken(token, ACCESS_TOKEN_PREFIX);
}

/**
 * refresh token → 用户 id。
 *
 * 前缀对但认不出账号时当成演示用户：老版本 Mock 发的 refresh token（没有用户后缀）
 * 就长这样，它还躺在用户浏览器的 localStorage 里。这样升级后 api-client 的
 * "40101 → 刷新 → 重放一次"能自愈，而不是把所有人一脚踢回登录页。
 */
export function userIdOfRefreshToken(token: string | null): string | null {
  const resolved = userIdOfToken(token, REFRESH_TOKEN_PREFIX);
  if (resolved !== null) {
    return resolved;
  }
  return token !== null && token.startsWith(REFRESH_TOKEN_PREFIX) ? MOCK_USER_ID : null;
}

/* ------------------------------------------------------------------ *
 * 额度与发放
 * ------------------------------------------------------------------ */

/** 用户额度的**原始计数**（只读视图）：演示用户读顶层，别的账号读自己那一行。 */
export function quotaStateOf(userId: string): MockUserQuota {
  const current = getMockState();
  if (userId === MOCK_USER_ID) {
    return {
      planId: current.planId,
      dailyTokenUsed: current.dailyTokenUsed,
      monthlyTokenUsed: current.monthlyTokenUsed,
      dailyResetAt: current.dailyResetAt,
      monthlyResetAt: current.monthlyResetAt,
    };
  }
  const quota = findMockUser(userId)?.quota;
  return (
    quota ?? {
      planId: DEFAULT_PLAN_ID,
      dailyTokenUsed: 0,
      monthlyTokenUsed: 0,
      dailyResetAt: current.dailyResetAt,
      monthlyResetAt: current.monthlyResetAt,
    }
  );
}

/** 当月发放总量。窗口是半开区间 `[startOfUtcMonth, nextMonthlyResetAt)`，与额度月度口径一致。 */
export function monthlyGrantedTokensOf(userId: string, now: Date): number {
  const from = new Date(startOfUtcMonth(now)).getTime();
  const to = new Date(nextMonthlyResetAt(now)).getTime();
  return getMockState()
    .grants.filter((grant) => {
      if (grant.userId !== userId) {
        return false;
      }
      const at = new Date(grant.createdAt).getTime();
      return at >= from && at < to;
    })
    .reduce((sum, grant) => sum + grant.totalTokens, 0);
}

/** 某用户的发放记录（后台配额页列它）。 */
export function grantsOf(userId: string): MockQuotaGrant[] {
  return getMockState()
    .grants.filter((grant) => grant.userId === userId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * 额度快照（对外 `Quota`）。
 *
 * 管理员 / ROOT 没有个人额度 → `null`（契约 v1.4.0 允许 `quota: null`，
 * 后台外壳也不渲染额度条）。
 *
 * 发放的额度**只抵扣已用量，不改上限**：可用 = 上限 − (已用 − 发放)。
 * 这与真实后端把 commit（计量事实）与 grant（授权事实）分两本账、只在算可用量时相减
 * 是同一套口径。
 */
export function quotaOfUser(userId: string, now: Date): Quota | null {
  const user = findMockUser(userId);
  if (user === null || user.role !== 'user') {
    return null;
  }
  const quota = quotaStateOf(userId);
  const granted = monthlyGrantedTokensOf(userId, now);
  return snapshotOf(quota.planId, quota.dailyTokenUsed, Math.max(0, quota.monthlyTokenUsed - granted), now);
}

/** 用户资料（管理员 / ROOT 为 null）。 */
export function profileOfUser(userId: string): Profile | null {
  return findMockUser(userId)?.profile ?? null;
}

/** 覆盖用户资料（`PATCH /profile`；取数层已经过 `ProfileSchema`）。 */
export function saveProfile(userId: string, profile: Profile): void {
  const user = findMockUser(userId);
  if (user === null) {
    return;
  }
  user.profile = profile;
  user.updatedAt = isoNow();
  persist();
}

/** 用户订阅：演示用户读顶层 `subscription`（支付流程写的就是它），别的账号读自己那一行。 */
export function subscriptionOfUser(userId: string): Subscription | null {
  if (userId === MOCK_USER_ID) {
    return getMockState().subscription;
  }
  return findMockUser(userId)?.subscription ?? null;
}

/**
 * 窗口 `[from, to)` 内的结算行。
 *
 * 只统计 `kind === 'commit'`：token 消耗是**已发生**的计费事实，预扣/退补都不是。
 * 也**不按软删过滤**——会话删了，已经烧掉的 token 不会回来（与真实后端
 * `sumCommittedBetween` / `aggregateUsage` 的例外口径一致）。
 */
export function committedEntriesBetween(from: Date, to: Date): MockLedgerEntry[] {
  return getMockState().ledger.filter((entry) => {
    if (entry.kind !== 'commit') {
      return false;
    }
    const at = new Date(entry.createdAt).getTime();
    return at >= from.getTime() && at < to.getTime();
  });
}

/**
 * 今日 / 本月的消耗合计（UTC 切片）。
 *
 * 用 `createdAt` 的 UTC 日期前缀分组，而不是"距现在 24 小时"：契约的日/月额度都按
 * UTC 自然日切分（`QUOTA_RESET_TIMEZONE`），概览里的"今日 token"必须和额度页同口径。
 */
export function tokenTotalsOf(now: Date): { todayTokens: number; monthTokens: number } {
  const day = now.toISOString().slice(0, 10);
  const month = day.slice(0, 7);
  let todayTokens = 0;
  let monthTokens = 0;
  for (const entry of getMockState().ledger) {
    if (entry.kind !== 'commit') {
      continue;
    }
    const key = entry.createdAt.slice(0, 10);
    if (key === day) {
      todayTokens += entry.totalTokens;
    }
    if (key.startsWith(month)) {
      monthTokens += entry.totalTokens;
    }
  }
  return { todayTokens, monthTokens };
}

/** 某条助手消息的计费明细（后台消息列表按消息显示 token 量）。 */
export function usageOfMessage(
  messageId: string,
): { promptTokens: number; completionTokens: number; channelId: string | null } | null {
  const entry = getMockState().ledger.find((item) => item.messageId === messageId);
  if (entry === undefined) {
    return null;
  }
  return { promptTokens: entry.promptTokens, completionTokens: entry.completionTokens, channelId: entry.channelId };
}

/* ------------------------------------------------------------------ *
 * LLM 渠道
 * ------------------------------------------------------------------ */

/**
 * 渠道排序：`priority` 升序 → `createdAt` 升序 → `id` 升序。
 *
 * 与真实后端 `list()` / `findActive()` 的排序口径**逐字一致**（ports.ts 的注释要求）。
 * 两个模式排出不同顺序的话，"哪条渠道被选中"就会随模式变，而这种差异只在真机上才暴露。
 */
export function sortedMockChannels(): MockLlmChannel[] {
  return [...getMockState().llmChannels].sort(
    (a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
}

/**
 * 当前生效渠道（v1.6.0）：**候选顺序里第一条对话模型**所属的那条渠道；没有就返回 null。
 *
 * 不再是"enabled 里优先级最高的那个"：渠道是否真在承载流量由模型路由表决定
 * （后端 `AdminChannelsService.list()` 取的是 `upstream.chat[0].channelId`）。
 * 两种口径分叉时，渠道页会指着一个请求根本不会走的渠道。
 *
 * 返回 null 时 Mock 的对话由本地话术生成（`AdminAiStatusSchema.mock = true`），
 * 与开发环境一条候选都没有时的行为一致。
 */
export function activeMockChannel(): MockLlmChannel | null {
  const route = usableMockModelRoutes().find((item) => item.role === 'chat' || item.role === 'both');
  return route === undefined ? null : findMockChannel(route.channelId);
}

export function findMockChannel(id: string): MockLlmChannel | null {
  return getMockState().llmChannels.find((item) => item.id === id) ?? null;
}

/** 新建渠道（ROOT）。`apiKey` 必填——新建没有"不改密钥"这回事。 */
export function createMockChannel(input: UpsertLlmChannelInput): MockLlmChannel {
  const now = isoNow();
  const channel: MockLlmChannel = {
    id: nextId('ckmockchan'),
    name: input.name,
    provider: input.provider,
    baseUrl: input.baseUrl,
    apiKey: input.apiKey,
    enabled: input.enabled,
    priority: input.priority,
    timeoutMs: input.timeoutMs,
    remark: input.remark,
    createdAt: now,
    updatedAt: now,
  };
  getMockState().llmChannels.push(channel);
  persist();
  return channel;
}

/**
 * 局部更新渠道（ROOT）：只有**显式传了**才改。
 *
 * 用 `!== undefined` 而不是 `??`：`apiKey` 省略表示"不改密钥"（PATCH 语义），
 * `remark: null` 表示"清空备注"——用 `??` 会把后者当成没传而静默忽略。
 */
export function updateMockChannel(id: string, patch: PatchLlmChannelInput): MockLlmChannel | null {
  const channel = findMockChannel(id);
  if (channel === null) {
    return null;
  }
  if (patch.name !== undefined) {
    channel.name = patch.name;
  }
  if (patch.provider !== undefined) {
    channel.provider = patch.provider;
  }
  if (patch.baseUrl !== undefined) {
    channel.baseUrl = patch.baseUrl;
  }
  if (patch.apiKey !== undefined) {
    channel.apiKey = patch.apiKey;
  }
  if (patch.enabled !== undefined) {
    channel.enabled = patch.enabled;
  }
  if (patch.priority !== undefined) {
    channel.priority = patch.priority;
  }
  if (patch.timeoutMs !== undefined) {
    channel.timeoutMs = patch.timeoutMs;
  }
  if (patch.remark !== undefined) {
    channel.remark = patch.remark;
  }
  channel.updatedAt = isoNow();
  persist();
  return channel;
}

/**
 * 删除渠道（ROOT）。
 *
 * 连带删掉挂在它上面的模型路由：真实后端 `llm_model_routes.channelId` 是带
 * `ON DELETE CASCADE` 的外键（内存实现表现为"渠道没了 → 这些行不可见"），
 * 留下孤儿行的话，它们既不出现在页面上、又还占着 `move` 的重编号序号，
 * "上移一格"会莫名其妙地跳过一步。
 */
export function deleteMockChannel(id: string): boolean {
  const state = getMockState();
  const index = state.llmChannels.findIndex((item) => item.id === id);
  if (index < 0) {
    return false;
  }
  state.llmChannels.splice(index, 1);
  state.llmModelRoutes = state.llmModelRoutes.filter((route) => route.channelId !== id);
  persist();
  return true;
}

/* ------------------------------------------------------------------ *
 * 模型路由（v1.6.0 的「模型优先级」）
 * ------------------------------------------------------------------ */

/**
 * 重排后每行的优先级间距。
 *
 * 与后端 `AdminModelRoutesService` 的 `REORDER_STEP` **逐字一致**（那里写了为什么是
 * "整表编号"而不是"交换两行数字"）：新建行的 `priority` 默认都是 100，等值时交换是空操作，
 * ROOT 看到的是"点了上移没反应"。留空档是为了还能手工插一条 15 进去。
 */
const REORDER_STEP = 10;

/**
 * 路由 + 渠道的配对行，按**全局候选顺序**排好。
 *
 * 排序与后端 `compareModelRoutes()` / `MODEL_ROUTE_ORDER_BY` 逐条对齐：
 * `route.priority asc → 渠道 priority asc → createdAt asc → id asc`。
 * 四段全确定，任何一段不唯一都会让同一份配置在两次请求里给出不同候选顺序，
 * 降级路径就没法复现——两套实现（Prisma / 内存）分叉更是只在真机上才暴露。
 *
 * 渠道已删的行在这里被丢掉（对应外键级联），调用方永远看不到孤儿行。
 */
function modelRouteRows(): Array<{ route: MockLlmModelRoute; channel: MockLlmChannel }> {
  const state = getMockState();
  const rows: Array<{ route: MockLlmModelRoute; channel: MockLlmChannel }> = [];
  for (const route of state.llmModelRoutes) {
    const channel = state.llmChannels.find((item) => item.id === route.channelId);
    if (channel !== undefined) {
      rows.push({ route, channel });
    }
  }
  return rows.sort(
    (a, b) =>
      a.route.priority - b.route.priority ||
      a.channel.priority - b.channel.priority ||
      a.route.createdAt.localeCompare(b.route.createdAt) ||
      a.route.id.localeCompare(b.route.id),
  );
}

/** 全部模型路由（含停用行），按全局顺序。分页与"上移一格"都以它为基准。 */
export function sortedMockModelRoutes(): LlmModelRoute[] {
  return modelRouteRows().map(({ route, channel }) => modelRouteDtoOf(route, channel));
}

/**
 * **可用**候选（对话 / 意图路由共用）：与 `AiUpstreamResolver.load()` 同一套过滤。
 *
 * 依次丢掉：停用的路由、渠道已删或已停用的、以及渠道密钥为空的（真实后端那里是
 * "密文解不开"，原因是 `LLM_CHANNEL_SECRET` 被换过）。少任何一条过滤，
 * 页面顶部的"当前生效模型"就会指着一个实际请求根本不会用的模型。
 */
export function usableMockModelRoutes(): LlmModelRoute[] {
  return modelRouteRows()
    .filter(({ route, channel }) => route.enabled && channel.enabled && channel.apiKey.length > 0)
    .map(({ route, channel }) => modelRouteDtoOf(route, channel));
}

export function findMockModelRoute(id: string): MockLlmModelRoute | null {
  return getMockState().llmModelRoutes.find((item) => item.id === id) ?? null;
}

/** 同一渠道下是否已有同名模型（`(channelId, model)` 唯一）。`exceptId` 用于 PATCH 排除自己。 */
export function existsMockModelRoute(channelId: string, model: string, exceptId: string | null = null): boolean {
  return getMockState().llmModelRoutes.some(
    (route) => route.channelId === channelId && route.model === model && route.id !== exceptId,
  );
}

/**
 * 新建模型路由（ROOT）。
 *
 * 这里**不**校验渠道是否存在、也不查重：那两条是业务规则（要翻译成 40400 / 40900
 * 两个不同的错误码），由 `mock-admin.ts` 在路由层判——与 `createMockChannel()` 的分工一致。
 */
export function createMockModelRoute(input: UpsertLlmModelRouteInput): MockLlmModelRoute {
  const now = isoNow();
  const route: MockLlmModelRoute = {
    id: nextId('ckmockroute'),
    channelId: input.channelId,
    model: input.model,
    role: input.role,
    enabled: input.enabled,
    priority: input.priority,
    remark: input.remark,
    createdAt: now,
    updatedAt: now,
  };
  getMockState().llmModelRoutes.push(route);
  persist();
  return route;
}

/** 局部更新（ROOT）：只有显式传了才改；`remark: null` 表示清空备注。 */
export function updateMockModelRoute(id: string, patch: PatchLlmModelRouteInput): MockLlmModelRoute | null {
  const route = findMockModelRoute(id);
  if (route === null) {
    return null;
  }
  if (patch.channelId !== undefined) {
    route.channelId = patch.channelId;
  }
  if (patch.model !== undefined) {
    route.model = patch.model;
  }
  if (patch.role !== undefined) {
    route.role = patch.role;
  }
  if (patch.enabled !== undefined) {
    route.enabled = patch.enabled;
  }
  if (patch.priority !== undefined) {
    route.priority = patch.priority;
  }
  if (patch.remark !== undefined) {
    route.remark = patch.remark;
  }
  route.updatedAt = isoNow();
  persist();
  return route;
}

export function deleteMockModelRoute(id: string): boolean {
  const routes = getMockState().llmModelRoutes;
  const index = routes.findIndex((item) => item.id === id);
  if (index < 0) {
    return false;
  }
  routes.splice(index, 1);
  persist();
  return true;
}

/**
 * 上移 / 下移一行，并把全局顺序固化成 `REORDER_STEP` 的等间距编号。
 *
 * 边界（首行上移 / 末行下移）**不做任何事**并返回 false：接口层据此返回原列表而不报错
 * （按钮置灰是 UI 细节，接口不该因为"没什么可做"失败）。列表里包含停用的行，
 * 否则"上移一格"会跳过看不见的行——页面展示的顺序与移动时看到的顺序必须是同一个。
 *
 * 只写值真的变了的行：首次移动后各行都落在间距 10 的格点上，之后通常只改 2 行。
 */
export function moveMockModelRoute(id: string, direction: 'up' | 'down'): boolean {
  const records = modelRouteRows();
  const from = records.findIndex((record) => record.route.id === id);
  if (from < 0) {
    return false;
  }
  const to = direction === 'up' ? from - 1 : from + 1;
  if (to < 0 || to >= records.length) {
    return false;
  }
  const moved = records[from];
  if (moved === undefined) {
    return false;
  }
  const reordered = records.filter((record) => record.route.id !== id);
  reordered.splice(to, 0, moved);

  reordered.forEach((record, index) => {
    const desired = (index + 1) * REORDER_STEP;
    if (record.route.priority === desired) {
      return;
    }
    const stored = findMockModelRoute(record.route.id);
    if (stored !== null) {
      stored.priority = desired;
      stored.updatedAt = isoNow();
    }
  });
  persist();
  return true;
}

/* ------------------------------------------------------------------ *
 * 邮箱渠道（邮箱验证码登录的发信通道）
 * ------------------------------------------------------------------ */

/**
 * 排序口径与 LLM 渠道逐字一致：`priority` 升序 → `createdAt` → `id`。
 *
 * 两个列表页共用同一套"哪条在生效"的读法，口径分叉时屏幕上会出现
 * "列表第一条不是生效那条"这种只在特定数据下才现形的错。
 */
export function sortedMockEmailChannels(): MockEmailChannel[] {
  return [...getMockState().emailChannels].sort(
    (a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
}

/** 当前生效渠道：优先级最高**且启用**的那一条；没有就返回 null（发信退回模拟）。 */
export function activeMockEmailChannel(): MockEmailChannel | null {
  return sortedMockEmailChannels().find((channel) => channel.enabled) ?? null;
}

export function findMockEmailChannel(id: string): MockEmailChannel | null {
  return getMockState().emailChannels.find((item) => item.id === id) ?? null;
}

/** 新建邮箱渠道（ROOT）。`apiKey` 必填——新建没有"不改密钥"这回事。 */
export function createMockEmailChannel(input: UpsertEmailChannelInput): MockEmailChannel {
  const now = isoNow();
  const channel: MockEmailChannel = {
    id: nextId('ckmockmailchan'),
    name: input.name,
    provider: input.provider,
    baseUrl: input.baseUrl,
    apiKey: input.apiKey,
    fromAddress: input.fromAddress,
    fromName: input.fromName,
    enabled: input.enabled,
    priority: input.priority,
    timeoutMs: input.timeoutMs,
    remark: input.remark,
    createdAt: now,
    updatedAt: now,
  };
  getMockState().emailChannels.push(channel);
  persist();
  return channel;
}

/**
 * 局部更新邮箱渠道（ROOT）：只有**显式传了**才改。
 *
 * 与 `updateMockChannel()` 同一个理由用 `!== undefined`：`apiKey` 省略 = 不改密钥，
 * `remark: null` = 清空备注，用 `??` 会把后者当成没传。
 */
export function updateMockEmailChannel(id: string, patch: PatchEmailChannelInput): MockEmailChannel | null {
  const channel = findMockEmailChannel(id);
  if (channel === null) {
    return null;
  }
  if (patch.name !== undefined) {
    channel.name = patch.name;
  }
  if (patch.provider !== undefined) {
    channel.provider = patch.provider;
  }
  if (patch.baseUrl !== undefined) {
    channel.baseUrl = patch.baseUrl;
  }
  if (patch.apiKey !== undefined) {
    channel.apiKey = patch.apiKey;
  }
  if (patch.fromAddress !== undefined) {
    channel.fromAddress = patch.fromAddress;
  }
  if (patch.fromName !== undefined) {
    channel.fromName = patch.fromName;
  }
  if (patch.enabled !== undefined) {
    channel.enabled = patch.enabled;
  }
  if (patch.priority !== undefined) {
    channel.priority = patch.priority;
  }
  if (patch.timeoutMs !== undefined) {
    channel.timeoutMs = patch.timeoutMs;
  }
  if (patch.remark !== undefined) {
    channel.remark = patch.remark;
  }
  channel.updatedAt = isoNow();
  persist();
  return channel;
}

export function deleteMockEmailChannel(id: string): boolean {
  const channels = getMockState().emailChannels;
  const index = channels.findIndex((item) => item.id === id);
  if (index < 0) {
    return false;
  }
  channels.splice(index, 1);
  persist();
  return true;
}

/* ------------------------------------------------------------------ *
 * 后台写操作（封号 / 发放 / 改套餐 / 后台账号）
 * ------------------------------------------------------------------ */

/**
 * 封号。
 *
 * 临时封号算出到期时刻；长期封号写 null。契约 `AdminCountFilter.suspensionActiveAt`
 * 的语义就是靠这个区分：`suspended_until IS NULL` 表示永久生效。
 */
export function suspendMockUser(userId: string, input: SuspendUserInput, now: Date): MockUser | null {
  const user = findMockUser(userId);
  if (user === null) {
    return null;
  }
  user.status = 'suspended';
  user.suspendedAt = now.toISOString();
  user.suspendedUntil =
    input.kind === 'temporary'
      ? new Date(now.getTime() + (input.durationHours ?? 0) * 3_600_000).toISOString()
      : null;
  user.suspendReason = input.reason;
  user.updatedAt = now.toISOString();
  persist();
  return user;
}

/** 解封：三个封号字段一起清空（只清 `status` 会让后台继续显示"封号到 X 时"）。 */
export function unsuspendMockUser(userId: string, now: Date): MockUser | null {
  const user = findMockUser(userId);
  if (user === null) {
    return null;
  }
  user.status = 'active';
  user.suspendedAt = null;
  user.suspendedUntil = null;
  user.suspendReason = null;
  user.updatedAt = now.toISOString();
  persist();
  return user;
}

/** 发放额度：只记一笔账，不动上限（可用量在 `quotaOfUser()` 里抵扣）。 */
export function applyGrant(userId: string, input: GrantQuotaInput, grantedByUserId: string, now: Date): void {
  getMockState().grants.push({
    id: nextId('ckmockgrant'),
    userId,
    totalTokens: input.totalTokens,
    reason: input.reason,
    grantedByUserId,
    createdAt: now.toISOString(),
  });
  persist();
}

/**
 * 管理员直接改套餐（`PUT /admin/users/:id/plan`）。
 *
 * 与用户自己下单的区别：这里没有真实订单，但契约的 `Subscription.orderNo` 是**必填**，
 * 所以用 `nextOrderNo()` 造一个可报给客服的号，而不是塞 null 让契约校验直接失败。
 */
export function setPlanForUser(userId: string, input: SetUserPlanInput, now: Date): void {
  const startsAt = now.toISOString();
  const subscription: Subscription = {
    planId: input.planId,
    billingCycle: input.billingCycle,
    startsAt,
    expiresAt: new Date(now.getTime() + input.durationDays * 86_400_000).toISOString(),
    orderNo: nextOrderNo(),
  };

  if (userId === MOCK_USER_ID) {
    // 演示用户的额度与订阅都在顶层：走 `activateSubscription()` 保证两者一起改
    //（只改一个就会出现"额度页说 Pro、购买页说 Free"）。
    activateSubscription(subscription);
    return;
  }

  const user = findMockUser(userId);
  if (user === null) {
    return;
  }
  user.subscription = subscription;
  if (user.quota === null) {
    user.quota = {
      planId: input.planId,
      dailyTokenUsed: 0,
      monthlyTokenUsed: 0,
      dailyResetAt: nextDailyResetAt(now),
      monthlyResetAt: nextMonthlyResetAt(now),
    };
  } else {
    user.quota.planId = input.planId;
  }
  user.updatedAt = startsAt;
  persist();
}

/** 新建后台账号（ROOT）。账号名已存在时返回 `'conflict'`，由路由层翻成 40900。 */
export function createMockAdmin(input: CreateAdminInput, now: Date): MockUser | 'conflict' {
  if (findMockUserByUsername(input.username) !== null) {
    return 'conflict';
  }
  const state = getMockState();
  const user: MockUser = {
    // 用户 id 是纯数字字符串（v1.4.1），不能用 nextId('ckmockadmin') 那种 cuid 夹具
    id: nextUserId(state.users),
    role: 'admin',
    nickname: input.nickname,
    phone: null,
    // 后台账号只走账号口令，不绑邮箱（契约里 email 本就 nullable）。
    email: null,
    wechatOpenId: null,
    avatarUrl: null,
    status: 'active',
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    username: input.username,
    password: input.password,
    passwordUpdatedAt: now.toISOString(),
    suspendedAt: null,
    suspendedUntil: null,
    suspendReason: null,
    lastLoginAt: null,
    quota: null,
    subscription: null,
    profile: null,
  };
  state.users.push(user);
  persist();
  return user;
}

/** 删除后台账号（ROOT）。"不能删 ROOT / 不能删自己"由路由层挡在前面（那里才有文案）。 */
export function deleteMockAdmin(userId: string): boolean {
  const users = getMockState().users;
  const index = users.findIndex((item) => item.id === userId);
  if (index < 0) {
    return false;
  }
  users.splice(index, 1);
  persist();
  return true;
}

/** 重置后台口令（ROOT）。 */
export function resetMockAdminPassword(userId: string, password: string, now: Date): boolean {
  const user = findMockUser(userId);
  if (user === null) {
    return false;
  }
  user.password = password;
  user.passwordUpdatedAt = now.toISOString();
  user.updatedAt = now.toISOString();
  persist();
  return true;
}

/** 某用户的订单（后台套餐详情用）：靠 `orderOwners` 反查，最新在前。 */
export function ordersOfUser(userId: string): Order[] {
  const current = getMockState();
  return current.orders
    .filter((order) => current.orderOwners[order.orderNo] === userId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.orderNo.localeCompare(a.orderNo));
}
